import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import CustomTable from '../../../../../components/CustomTable';
import CustomModal from '../../../../../components/CustomModal';
import CustomInput from '../../../../../components/CustomInput';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { toast } from 'react-toastify';
import {
    allSlackDrivers,
    slackDriversLoadingStatus,
    getSlackDrivers,
    createSlackDriver,
    deleteSlackDriver,
    updateSlackDriver,
} from '../../../../../redux/slices/settingsSlice';

export default function SlackDriversSubPage() {
    const dispatch = useDispatch();

    const slackDriversStatus = useSelector(slackDriversLoadingStatus);
    const slackDrivers = useSelector(allSlackDrivers);

    const [isCreateDriverModalVisible, setIsCreateDriverModalVisible] =
        useState(false);
    const [isEditDriverModalVisible, setIsEditDriverModalVisible] =
        useState(false);
    const [isDeleteDriverModalVisible, setIsDeleteDriverModalVisible] =
        useState(false);
    const [data, setData] = useState({
        channel_name: '',
        url: '',
    });
    const [selectedDriver, setSelectedDriver] = useState({
        id: null,
        name: '',
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getSlackDrivers());
    }, [dispatch]);

    const resetState = () => {
        setData({
            channel_name: '',
            url: '',
        });
    };

    const toggleCreateDriverModal = () => {
        resetState();
        setErrors({});
        setIsCreateDriverModalVisible(!isCreateDriverModalVisible);
    };

    const toggleEditDriverModal = (record) => {
        if (record === undefined) {
            setIsEditDriverModalVisible(!isEditDriverModalVisible);
        } else {
            setData({
                channel_name: record.channel_name,
                url: record.url,
            });
            setSelectedDriver({ id: record.id, name: record.channel_name });
            setErrors({});
            setIsEditDriverModalVisible(!isEditDriverModalVisible);
        }
    };

    const toggleDeleteDriverModal = (record) => {
        if (record === undefined) {
            setIsDeleteDriverModalVisible(!isDeleteDriverModalVisible);
        } else {
            setSelectedDriver({ id: record.id, name: record.channel_name });
            setIsDeleteDriverModalVisible(!isDeleteDriverModalVisible);
        }
        setErrors({});
    };

    const onInputChange = (e) => {
        const value = e.target.value;
        setData({
            ...data,
            [e.target.id]: value,
        });
    };

    const onCreateDriver = async () => {
        try {
            await dispatch(createSlackDriver(data)).unwrap();
            toast.success('New Slack driver successfully added');
            toggleCreateDriverModal();
            dispatch(getSlackDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onEditDriver = async () => {
        try {
            await dispatch(
                updateSlackDriver({ id: selectedDriver.id, body: data }),
            ).unwrap();
            toast.success('Changes saved successfully');
            toggleEditDriverModal();
            dispatch(getSlackDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteDriver = async () => {
        try {
            await dispatch(deleteSlackDriver(selectedDriver.id)).unwrap();
            toast.success('Slack driver successfully deleted');
            toggleDeleteDriverModal();
            dispatch(getSlackDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const getFilteredDrivers = (data, filter) => {
        const filteredData = data.filter((el) =>
            el?.channel_name.toLowerCase().includes(filter.toLowerCase()),
        );

        return filteredData;
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined onClick={(e) => toggleEditDriverModal(record)} />
                <DeleteOutlined
                    onClick={(e) => toggleDeleteDriverModal(record)}
                />
            </Space>
        ),
    };

    return (
        <div>
            <Helmet>
                <title>
                    Console Defentry - Settings - Notifications - Slack drivers
                </title>
            </Helmet>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={['Channel name', 'URL', 'Actions']}
                    functionalCell={{ Actions: actionsOption }}
                    onAddButtonClick={toggleCreateDriverModal}
                    tablePannelTitle="List of drivers"
                    searchInputPlaceholder="Search by name"
                    topPannelButtonText="Create driver"
                    data={slackDrivers}
                    getFilteredData={getFilteredDrivers}
                    isLoading={slackDriversStatus === 'loading'}
                    withFilter
                />
            </div>
            <CustomModal
                title="Create driver"
                subText="Please fill the fields below"
                subTextAlign="left"
                isModalVisible={isCreateDriverModalVisible}
                onAcceptButtonClick={onCreateDriver}
                onCancelButtonClick={toggleCreateDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Create"
            >
                <div className="form__item-wrapper">
                    <CustomInput
                        id="channel_name"
                        placeholder="Enter channel name"
                        onInputChange={onInputChange}
                        inputValue={data.channel_name}
                        labelText="Channel name"
                        width="100%"
                        errorText={errors?.channel_name}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="url"
                        placeholder="Enter URL"
                        onInputChange={onInputChange}
                        inputValue={data.url}
                        labelText="URL"
                        width="100%"
                        errorText={errors?.url}
                    />
                </div>
            </CustomModal>
            <CustomModal
                title="Edit driver"
                subText="Please fill the fields below"
                subTextAlign="left"
                isModalVisible={isEditDriverModalVisible}
                onAcceptButtonClick={onEditDriver}
                onCancelButtonClick={toggleEditDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Update"
            >
                <div className="form__item-wrapper">
                    <CustomInput
                        id="channel_name"
                        placeholder="Enter channel name"
                        onInputChange={onInputChange}
                        inputValue={data.channel_name}
                        labelText="Channel name"
                        width="100%"
                        errorText={errors?.channel_name}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="url"
                        placeholder="Enter URL"
                        onInputChange={onInputChange}
                        inputValue={data.url}
                        labelText="URL"
                        width="100%"
                        errorText={errors?.url}
                    />
                </div>
            </CustomModal>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${selectedDriver.name} `}</span>
                        driver?
                    </p>
                }
                subText="You will delete this driver without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteDriverModalVisible}
                onAcceptButtonClick={onDeleteDriver}
                onCancelButtonClick={toggleDeleteDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            >
                {errors.slack_driver_id && (
                    <p className="modal__error-text">
                        {errors.slack_driver_id[0]}
                    </p>
                )}
            </CustomModal>
        </div>
    );
}
